//TODO: check this needed reference. Without it Error Page
// eslint-disable-next-line no-unused-vars
import '../../services/localization/localizationService';

import appConfig from '../../config/appConfig';
import {makeSelectIotDeviceData, selectIsIotDeviceDataExists} from '../../state/selectors/iotDevice';
import {selectMwDeviceInternalId} from '../../state/selectors/mwDevice';
import {getState} from '../../state/store';
import appInstanceService from '../app/appInstanceService';
import backendService from '../app/backendService';
import authDataStoreService from '../auth/authDataStoreService';
import cloudWatchProvider from '../aws/cloudWatchProvider';
import connectivityService from '../device/connectivityService';
import storageService from '../storage/storageService';

const logLevels = {
    DEBUG: {
        id: 1,
        name: 'DEBUG',
    },
    INFO: {
        id: 2,
        name: 'INFO',
    },
    ERROR: {
        id: 3,
        name: 'ERROR',
    },
    SILENT: {
        id: 4,
        name: 'SILENT',
    },
    FATAL: {
        id: 5,
        name: 'FATAL',
    },
};

let instance = null;

const LOG_COUNT_TO_PUSH = 10;
const TIME_TO_PUSH_MS = 5 * 1000;
const PUT_EVENTS_ERROR_ATTEMPTS = 5;

class SmartLogger {
    constructor() {
        if (instance) {
            return instance;
        }

        instance = this;
    }

    logsExposeTimeout = null;
    queue = [];
    isPushInProgress = false;
    currentPutEventsErrorAttempts = 0;
    isLogExposePossible = true;

    level = logLevels.SILENT.id;

    setLevel = (level) => (this.level = level);
    getLevel = () => logLevels[this.level]?.id;
    debug = (msg, logMessageType) => this.log(logLevels.DEBUG, msg, logMessageType);
    error = (msg, logMessageType) => this.log(logLevels.ERROR, msg ? msg : 'Unknown message', logMessageType);
    info = (msg, logMessageType) => this.log(logLevels.INFO, msg, logMessageType);
    fatal = (msg, logMessageType) => this.log(logLevels.FATAL, msg, logMessageType);
    resetLogger = () => {
        this.currentPutEventsErrorAttempts = 0;
        this.isLogExposePossible = true;
    };

    log = (level, msg, logMessageType) => {
        const isLoggingAvailable = appConfig.getIsAwsLoggingEnabled() && level.id >= this.getLevel();

        if (isLoggingAvailable) {
            const logMessage = this.getFullLogMessage(level.name, msg, logMessageType);

            this.addToQueue(logMessage);

            if (this.isLogExposePossible) {
                if (this.queue.length >= LOG_COUNT_TO_PUSH - 1 || level >= logLevels.ERROR) {
                    this.exposeEvents();
                } else {
                    this.setTimeOutIfNeeded();
                }
            }
        }
    };

    addToQueue = (message) =>
        this.queue.push({
            message,
            timestamp: Date.now(),
        });

    getDynamicTimeout = () => {
        if (this.currentPutEventsErrorAttempts++ >= PUT_EVENTS_ERROR_ATTEMPTS) {
            this.isLogExposePossible = false;
            return false;
        }
        return this.currentPutEventsErrorAttempts;
    };

    exposeEvents = async () => {
        if (this.isPushInProgress) {
            this.setTimeOutIfNeeded();
        } else {
            if (this.queue.length) {
                this.isPushInProgress = true;
                const logsToPush = this.queue.splice(0, this.queue.length);

                await cloudWatchProvider.putEvents({
                    eventMessages: logsToPush,
                    onError: () => {
                        this.queue.unshift(...logsToPush); //return logs to queue
                        this.isPushInProgress = false;

                        this.setTimeOutIfNeeded(this.getDynamicTimeout());
                    },
                    onComplete: () => {
                        this.currentPutEventsErrorAttempts = 0;
                        this.isPushInProgress = false;
                    },
                });
            }
        }
    };

    collectData = () => {
        const consumerId = authDataStoreService.getConsumerId();
        const deviceSerialNumber = selectMwDeviceInternalId(getState());
        const appInstanceId = appInstanceService.getAppInstanceId();
        const isGamBackend = backendService.isGamBackend();
        const isIotDeviceDataExists = selectIsIotDeviceDataExists(getState());
        const isDeviceConnected = deviceSerialNumber || isIotDeviceDataExists;
        const connectionType = isDeviceConnected ? connectivityService.getConnectionTypeName() : undefined;
        const now = new Date();
        const market = appConfig.getScpCloudTopicMarket()?.toLowerCase();
        const marketPrefix = appConfig.getScpCloudTopicMarketPrefix()?.toLowerCase();
        const marketName = marketPrefix ? `${market}_${marketPrefix}` : market;
        let dusn = deviceSerialNumber;

        if (isGamBackend) {
            const deviceFromState = makeSelectIotDeviceData()(getState());
            const {codentify} = deviceFromState?.device || {};

            if (codentify) {
                dusn = codentify;
            } else {
                const deviceDataFromLocalStorage = storageService.getDeviceCodentifyFactoryFromLocalStorage();
                const {codentify} = deviceDataFromLocalStorage || {};

                dusn = codentify;
            }
        }

        return {
            app_id: appInstanceId,
            connection_type: this.getLogValue(connectionType),
            consumer_id: this.getLogValue(consumerId),
            date: now.toISOString(),
            dusn: this.getLogValue(dusn),
            environment: appConfig.getEnv(),
            iqos_connect_version: appConfig.getAppVersion(),
            market: marketName,
            timestamp: now.getTime(),
        };
    };

    getLogValue = (prop) => (prop !== undefined ? prop : 'n/a');

    getFullLogMessage = (level, message, logMessageType) => {
        const logData = this.collectData();
        const fullLog = {
            ...logData,
            level,
            message_scope: logMessageType,
            message,
        };
        const logMessage = JSON.stringify(fullLog);

        return logMessage;
    };

    setTimeOutIfNeeded = (timeoutMultiplier) => {
        const timeout = timeoutMultiplier ? timeoutMultiplier * TIME_TO_PUSH_MS : TIME_TO_PUSH_MS;

        if (this.queue.length && !this.logsExposeTimeout && this.isLogExposePossible) {
            this.logsExposeTimeout = setTimeout(() => {
                this.logsExposeTimeout = null;
                this.exposeEvents();
            }, timeout);
        }
    };
}

export default SmartLogger;
